import { Alert, Box, Typography } from "@mui/material";
import React from "react";

interface NetworkErrorDisplayProps {
  /**
   * The error message to display
   */
  errorMessage: string;

  /**
   * Callback function to retry the network request
   */
  onRetry: () => void;

  /**
   * Optional: Custom text for the retry button (defaults to "Retry Connection")
   */
  retryButtonText?: string;

  /**
   * Optional: Show the "Refresh Page" button (defaults to true)
   */
  showRefreshButton?: boolean;

  /**
   * Optional: Custom title (defaults to "Network Error")
   */
  title?: string;

  /**
   * Optional: Custom help text (defaults to standard troubleshooting message)
   */
  helpText?: string;
}

/**
 * NetworkErrorDisplay component for showing consistent network error messages
 * across the application. Used when subscription or other API requests fail.
 */
export const NetworkErrorDisplay: React.FC<NetworkErrorDisplayProps> = ({
  errorMessage,
  onRetry: _onRetry,
  retryButtonText: _retryButtonText = "Retry Connection",
  showRefreshButton: _showRefreshButton = true,
  title = "Network Error",
  helpText = "Please check your internet connection and try again. If the problem persists, you may need to refresh the page or sign out and sign back in.",
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Alert severity="error" sx={{ maxWidth: 600 }}>
        <Typography variant="body1" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {errorMessage}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {helpText}
        </Typography>
      </Alert>
    </Box>
  );
};
